import React, { ReactNode } from 'react';
import { PipelineOverviewService } from '../../../../../services/applicant_tracking/analytics/PipelineOverviewService';
import { PipelineOverview } from '../../../../../entities/applicant_tracking/analytics/PipelineOverview';
import { LoadingSpinner } from '../../../../../components/LoadingSpinner';
import { InfoTooltip } from '../../../../../components/InfoTooltip';
import { FilterState } from '../../Filters';
import { truncateNumber } from '../../../../../utils/number';
import { TruncateText } from '../../../../../components/TruncateText';
import { useAnalyticsData } from '../useAnalyticsData';
import { buildPassThroughRateValue } from '../../../../../utils/applicant_tracking/analytics';
import { WidgetEmptyStateContent } from '../../../../survey_engine/WidgetEmptyStateContent';
import styles from './styles.module.scss';

interface PropTypes {
  filterState?: FilterState;
}

export function PipelineOverviewWidget(props: PropTypes) {
  return <LazyContent filterState={props.filterState} />;
}

function isEmpty(data: PipelineOverview): boolean {
  return (
    data.passThroughRate == null &&
    data.rejectionRate == null &&
    data.averageTimeToHire == null &&
    data.averageFillHireGap == null
  );
}

function LazyContent(props: { filterState?: FilterState }) {
  const data = useAnalyticsData(
    PipelineOverviewService.show,
    props.filterState,
  );

  if (!data) return <LoadingSpinner />;

  if (isEmpty(data)) return <WidgetEmptyStateContent />;

  return <Content data={data} />;
}

function stageRangeLabel(data: PipelineOverview): string {
  const first = data.firstStageName ?? 'Applied';
  const last = data.lastStageName ?? 'Hired';
  return `From ${first} to ${last}`;
}

function Content(props: { data: PipelineOverview }) {
  const { data } = props;

  return (
    <div className={styles.container}>
      <MetricCard
        title='Overall Pass-Through Rate'
        tooltip='Percentage of applicants who were ultimately hired'
        footer={<SubLabel>{stageRangeLabel(data)}</SubLabel>}
      >
        <PercentageValue value={data.passThroughRate} />
      </MetricCard>
      <MetricCard
        title='Overall Rejection Rate'
        tooltip='Percentage of applicants who were rejected'
        footer={<SubLabel>Candidate Rejections</SubLabel>}
      >
        <PercentageValue value={data.rejectionRate} />
      </MetricCard>
      <MetricCard
        title='AVG Time to Hire'
        tooltip='Average number of days from application to hire'
        footer={<SubLabel>{stageRangeLabel(data)}</SubLabel>}
      >
        <DaysValue value={data.averageTimeToHire} />
      </MetricCard>
      <MetricCard
        title='AVG Fill-Hire Gap'
        tooltip='Average time between job requisition creation and candidate entering pipeline'
        footer={
          <div className='d-flex gap-2'>
            <Badge>
              {data.averageTimeToFill != null
                ? `${truncateNumber(data.averageTimeToFill)} days to fill`
                : '- days to fill'}
            </Badge>
            <Badge>
              {data.averageTimeToHire != null
                ? `${truncateNumber(data.averageTimeToHire)} days to hire`
                : '- days to hire'}
            </Badge>
          </div>
        }
      >
        <DaysValue value={data.averageFillHireGap} />
      </MetricCard>
    </div>
  );
}

function PercentageValue(props: { value?: number }) {
  if (props.value == null) return <EmptyValue />;

  return <MainValue>{buildPassThroughRateValue(props.value)}%</MainValue>;
}

function DaysValue(props: { value?: number }) {
  if (props.value == null) return <EmptyValue />;

  return <MainValue>{truncateNumber(props.value)} Days</MainValue>;
}

function EmptyValue() {
  return <div className='fs-2'>-</div>;
}

function MetricCard(props: {
  title: string;
  children: ReactNode;
  tooltip?: string;
  footer?: ReactNode;
}) {
  return (
    <div className='bg-white rounded-3 px-3 py-3h shadow-sm d-flex flex-column'>
      <div className='d-flex align-items-center gap-2'>
        <TruncateText
          tooltip={true}
          text={props.title}
          className='fs-5 fw-semibold'
        />
        {props.tooltip && <InfoTooltip>{props.tooltip}</InfoTooltip>}
      </div>
      {props.children}
      {props.footer && <div className='mt-auto pt-2'>{props.footer}</div>}
    </div>
  );
}

function MainValue(props: { children: ReactNode }) {
  return (
    <div className='fs-2 fw-bold' style={{ lineHeight: '24px' }}>
      {props.children}
    </div>
  );
}

function SubLabel(props: { children: ReactNode }) {
  return (
    <div
      className='text-dark-200 text-uppercase'
      style={{ fontSize: '10px', letterSpacing: '0.5px' }}
    >
      {props.children}
    </div>
  );
}

function Badge(props: { children: ReactNode }) {
  return (
    <span
      className='text-dark-200 rounded-pill px-2 py-1'
      style={{ fontSize: '10px', backgroundColor: '#F0F0F3' }}
    >
      {props.children}
    </span>
  );
}
